import React, {useEffect, useRef, useState} from 'react';
import produce from 'immer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Approvers.scss';
import { ErrorBoundary, ErrorMessage, ISearchDropdownAPI, Loader, Page, PageHeader, SearchDropdown, SearchItem2Line} from 'components';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, IconButton} from '@cfa/react-components';
import {axiosAPI, ISearcherOnSearchResponse, parseAxiosError, UsersService} from 'services';
import {IServiceNowUser, IUser} from 'interfaces';
import { AsyncStatus } from 'enums';

interface IApproversPageState {
    loadStatus?: AsyncStatus,
    loadError?: string,
    deleteStatus?: AsyncStatus,
    deleteError?: string,
    createStatus?: AsyncStatus;
    createError?: string;
    approvers?: IUser[],
    deleteApprover?: IUser,
}
export function Approvers() {

    const [pageState, setPageState] = useState<IApproversPageState>({
        loadStatus: AsyncStatus.Loading,
        createStatus: AsyncStatus.Initial,
        deleteStatus: AsyncStatus.Initial,
        approvers: [],
    })
    const { approvers, deleteStatus, loadStatus, createStatus, createError, deleteError, loadError, deleteApprover } = pageState;
    
    const [selectedApprover, setSelectedApprover] = useState<IServiceNowUser>();
    const approverDropdownRef = useRef<ISearchDropdownAPI>();

    // create modal control
    const [createShow, setCreateShow] = useState(false);
    const handleCreateClose = () => setCreateShow(false);
    const showCreateModal = () => {
        setCreateShow(true);
    };

    // delete modal control
    const [deleteShow, setDeleteShow] = useState(false);
    const handleDeleteClose = () => setDeleteShow(false);
    const showDeleteApproverModal = (a) => {
        setPageState(
            produce((draft) => {
                draft.deleteApprover = a;
            })
        );
        setDeleteShow(true);
    };

    // load data
    function loadApprovers() {
        const approversP = UsersService.getApprovers();

        approversP.then((approvers) => {
            setPageState(
                produce((draft) => {
                    draft.loadStatus = AsyncStatus.Success;
                    draft.approvers = approvers;
                })
            );
        }).catch((err) => {
            setPageState(
                produce((draft) => {
                    draft.loadStatus = AsyncStatus.Error;
                    draft.loadError = parseAxiosError(err);
                })
            );
        })
    }

    // get the initial approver list
    // TODO - data should be preloaded in app.tsx, so just assign from service
    useEffect(() => {
        loadApprovers();
    }, [])

    const limit = 20;

    function onApproverSearch(query: string, offset: number): ISearcherOnSearchResponse<IServiceNowUser> {
        const params = new URLSearchParams();
        params.append('search', query)
        params.append('limit', limit + '')
        params.append('offset', offset + '')

        return axiosAPI.get<IServiceNowUser[]>('/serviceNow/searchUsers?' + params.toString())
            .then((resp) => {
                const results = resp.data;
                return {
                    canLoadMore: results.length === limit,
                    results: results,
                }
            });
    }

    function onDeleteApprover(u: IUser) {
        if (deleteStatus === AsyncStatus.Loading) return;

        setPageState(
            produce((draft) => {
                draft.deleteApprover = u;
                draft.deleteError = '';
                draft.deleteStatus = AsyncStatus.Loading;
            })
        )

        UsersService.deleteApprover(deleteApprover.snowId)
            .then(() => {
                setPageState(
                    produce((draft) => {
                        draft.deleteApprover = undefined;
                        draft.deleteStatus = AsyncStatus.Success;
                        // TODO - use from cache
                        //draft.approvers = UsersService.approvers;
                    })
                )
                setDeleteShow(false)
                loadApprovers(); // TODO - use from cache
            })
            .catch((err) => {
                setPageState(
                    produce((draft) => {
                        draft.deleteError = parseAxiosError(err);
                        draft.deleteStatus = AsyncStatus.Error;
                    })
                )
            });
    }

    function onCreateApprover() {
        if (createStatus === AsyncStatus.Loading) return;

        // return if user is already an approver
        const approverIds = approvers.map((approver) => approver.snowId);
        if(approverIds.includes(selectedApprover.u_guid)) {
            setPageState(produce((draft) => {
                draft.createStatus = AsyncStatus.Error;
                draft.createError = `${selectedApprover.name} is already an approver.`;
            }))
            return;
        }
        
        setPageState(produce((draft) => {
            draft.createError = '';
            draft.createStatus = AsyncStatus.Loading;
        }));
        
        UsersService.postApprover(selectedApprover.sys_id)
            .then(() => {
                setPageState(
                    produce((draft) => {
                        draft.createError = '';
                        draft.createStatus = AsyncStatus.Success;
                        // TODO - use from cache
                        //draft.approvers = UsersService.approvers;
                    })
                )
                setCreateShow(false);
                loadApprovers(); // TODO - use from cache
            })
            .catch((err) => {
                setPageState(
                    produce((draft) => {
                        draft.createError = parseAxiosError(err);
                        draft.createStatus = AsyncStatus.Error;
                    })
                )
            });
        setSelectedApprover(undefined);
        approverDropdownRef.current.reset();
    }

    return (<>
        <Page>
            
            <PageHeader title="Approvers" right={<Button onClick={showCreateModal}>{'New Approver'}</Button>}/>

            <div className="row approvers_colHeaders">
                <div className="approvers_colHeader approvers_colName">Name</div>
                <div className="approvers_colHeader approvers_colEmailHeader">Email</div>
            </div>

            <div className="approvers_list">
                <ErrorBoundary>
                    {loadStatus === AsyncStatus.Loading && <Loader />}
                    {loadStatus === AsyncStatus.Success &&
                        approvers.length > 0 && approvers.map((approver, index) => {
                            return (
                                <div className="approvers_listItem" key={index}>
                                    <div className="approvers_colName">{approver.name}</div>
                                    <div className="approvers_colEmail">{approver.email}</div>
                                    <div>
                                        <IconButton
                                            size="sm"
                                            onClick={() => showDeleteApproverModal(approver)}
                                        >
                                            <FontAwesomeIcon icon="trash" />
                                        </IconButton>
                                    </div>
                                </div>
                            );
                        })}
                    {loadStatus === AsyncStatus.Success &&
                        approvers.length === 0 && (
                            <div className="approvers_listEmpty">
                                <FontAwesomeIcon
                                    icon="search"
                                    className="approvers_listEmptyIcon"
                                />
                                <div className="approvers_listEmptyText">
                                    No approvers found.
                                </div>
                            </div>
                        )}
                    {loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={loadError} />
                    )}
                </ErrorBoundary>
            </div>
        </Page>

        <Modal show={deleteShow} onClose={handleDeleteClose}>
            <ModalHeader>
                Remove Approver
            </ModalHeader>
            <ModalBody>
                <p>Are you sure you want to remove this user as an approver?</p>
                <div className="approvers_deleteHeader">
                    Name: {deleteApprover?.name}<br/>
                    Email: {deleteApprover?.email}
                </div>
            </ModalBody>
            <ModalFooter>
                {deleteStatus === AsyncStatus.Error && (
                    <ErrorMessage message={deleteError} />
                )}
                <Button variant="outlined" disabled={deleteStatus === AsyncStatus.Loading} onClick={handleDeleteClose}>
                    Cancel
                </Button>
                <Button loading={deleteStatus === AsyncStatus.Loading} onClick={() => onDeleteApprover(deleteApprover)}>
                    Remove
                </Button>
            </ModalFooter>
        </Modal>
        
        <Modal show={createShow} onClose={handleCreateClose}>
            <ModalHeader>
                Add Approver
            </ModalHeader>
            <ModalBody>
                <div>
                    {createStatus === AsyncStatus.Error && (
                        <ErrorMessage message={createError} />
                    )}
                    <div className="row">
                        <div className="col">
                            <div className="formItem_label">
                                Approver
                            </div>
                            <SearchDropdown<IServiceNowUser>
                                getOptionValue={user => user.u_guid}
                                onChange={setSelectedApprover}
                                onSearch={onApproverSearch}
                                renderOption={(option) => (
                                    <SearchItem2Line title={option.name} subtitle={option.email} />
                                )}
                                value={selectedApprover}
                                ref={approverDropdownRef}
                            />
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button variant="outlined" disabled={createStatus === AsyncStatus.Loading} onClick={handleCreateClose}>
                    Cancel
                </Button>
                <Button loading={createStatus === AsyncStatus.Loading} disabled={!selectedApprover} onClick={onCreateApprover}>
                    Add
                </Button>
            </ModalFooter>
        </Modal>

    </>);
}